import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import { NotificationFacade, RegisterFacade } from '@ridango/components';
import { AuthParametersFacade } from '@ridango/components/lib/auth/+state/auth-parameters';
import { RegisterWithEmailDTO, WidgetOptionsForEmailRegister } from '@ridango/core';
import Env from '@ridango/core/env';
import { ThemeFeature } from '@ridango/core/lib/enums/features.enum';

import { finalize, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { environment } from '../../../environments/environment';

@Component({
    templateUrl: './register-page.component.html',
    styleUrls: ['./register-page.component.scss']
})
export class RegisterPageComponent implements OnInit {
    private readonly EMAIL_REGISTRATION = 'Email registration';
    readonly captchaToggleAllowed = environment.captchaToggleAllowed;
    private componentDestroyed$ = new Subject<void>();
    themeFeature = ThemeFeature;
    steps$ = this.registerFacade.registrationSteps$;
    activeStep$ = this.registerFacade.registrationProgress$;
    terms = null;
    callback: string;
    allowRegister = true;

    // this should be in state
    registerSuccess = false;
    email = '';
    loading;
    registerOptions: WidgetOptionsForEmailRegister;

    constructor(
        @Inject('ENV') public env: Env,
        public translate: TranslateService,
        private registerFacade: RegisterFacade,
        private route: ActivatedRoute,
        private authParams: AuthParametersFacade,
        private notification: NotificationFacade,
        private analyticsService: AnalyticsService
    ) {}

    ngOnInit() {
        if (this.env.SIGN_ON_PAGE) {
            this.handleCallbackURL();
        }
        this.registerFacade.setRegistrationProgress(1);
        this.registerOptions = {
            locale: this.translate.currentLang,
            version: 1,
            showRequiredText: true,
            fullNameRequired: true
        };

        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event: LangChangeEvent) => {
            this.registerOptions.locale = event.lang;
        });
    }

    register(payload: RegisterWithEmailDTO) {
        if (this.loading) return;

        this.email = payload.email;
        payload.callbackUrl = this.callback;
        this.loading = true;

        this.registerFacade
            .registerWithEmailAndPassword({
                ...payload,
                language: this.translate.currentLang
            })
            .pipe(
                finalize(() => {
                    this.loading = false;
                })
            )
            .subscribe(() => {
                this.analyticsService.eventTrack(TrackedEventAction.COMPLETED, {
                    category: TrackedEventCategory.LOGIN,
                    name: this.EMAIL_REGISTRATION
                });
            });
    }

    handleCallbackURL() {
        this.authParams.callback$.pipe(take(2)).subscribe((callback) => {
            if (callback) {
                this.callback = callback;
            }
        });
        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            if (params.callback) {
                this.authParams.setCallback(params.callback);
            }
        });
        if (!this.callback) {
            this.allowRegister = false;
            this.notification.error(this.translate.instant('CALLBACK_MISSING'));
        }
    }

    handleBackClicked() {
        window.history.back();
    }
}
